import React, { useEffect } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faAt,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  faGithub,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';
import { makeStyles } from '@mui/styles';
import { BaseLayout } from '../BaseLayout';
import { useLanguage } from '../../localization';

// Base component for layouts with a header (centered, left, right)
const HeaderLayoutBase = ({
  formData,
  selectedSections,
  design, // Contains colors (standard or custom)
  typographySettings,
  typographyStyles,
  selectedStyle,
  activeStyle, // Added prop
  selectedBackground, // Use this prop to determine background
  activeBackgroundComponent, // Added prop for background component
  selectedSpecialEffectId, // Added prop
  activeBarStyle, // Add activeBarStyle prop
  currentPage = 0,
  totalPages = 1,
  measureMode = false,
  isPdfExport = false,
  onRenderComplete,
  headerAlignment = 'center', // 'center', 'left', 'right'
}) => {
  const { t } = useLanguage();
  
  useEffect(() => {
    if (typeof onRenderComplete === 'function') {
      onRenderComplete();
    }
  }, [onRenderComplete]);

  // Define common structural/design styles ONLY
  const useStyles = makeStyles({
    // Keep classes for structure (section, sectionContent, sectionItem, profilePhoto etc.)
    // Keep classes for design elements (contactIcon)
    profilePhoto: {
      width: '100px', // Standard size for profile photo
      height: '100px',
      margin: '0 auto 15px', // Centered margin for centered layout
      border: `3px solid ${design?.colors?.primary || '#000000'}`,
    },
    contactSection: { // Common styling for the contact items container
      display: 'flex',
      flexWrap: 'wrap',
      gap: '15px',
      marginTop: '10px',
    },
    contactIcon: {
      // Use design context color with fallback
      color: design?.colors?.primary || '#000',
      fontSize: '16px', // Keep fixed size or move to StyleContext later
      marginRight: '5px',
    },
    section: {
      marginBottom: '15px',
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },
    sectionHeader: { // Only structure/alignment/base border style here
      display: 'flex',
      alignItems: 'baseline',
      marginBottom: '10px',
      paddingBottom: '5px',
      // --- FIX: Add back border width and style ---
      borderBottomWidth: '2px', // Or your desired width
      borderBottomStyle: 'solid',
      // --- Color is set dynamically inline by BaseLayout.render* ---
    },
    sectionIcon: { // Icon specific styling
      marginRight: '8px',
      verticalAlign: 'middle',
       // Use design context color with fallback
      color: design?.colors?.secondary || '#000',
    },
    sectionContent: {
      // marginLeft: '25px', // Example structural style if needed
    },
    sectionItem: {
      marginBottom: '16px',
    },
  });

  const classes = useStyles();
  const { personalInfo = {} } = formData;

  // Determine header container styles based on alignment prop
  const getHeaderContainerStyles = () => {
    const baseStyles = {
        marginBottom: '20px', // Consistent spacing
        display: 'flex',
        justifyContent: 'space-between', // Pushes items apart
        alignItems: 'center', // Vertically center aligns the photo and the content block
    };
    switch (headerAlignment) {
        case 'left':
        case 'right': // --- Use 'row' for both ---
            // The render order will determine visual position
            return { ...baseStyles, flexDirection: 'row' }; 
        case 'center':
        default:
            // --- Keep centered styles unchanged ---
            return {
                marginBottom: '15px',
                textAlign: 'center', 
                display: 'block', 
            };
    }
  };

  // Header Content JSX (adapt structure slightly based on alignment needs)
  const renderHeaderContent = () => {
    const nameBlock = (
        <Box> {/* Wrap name and title */}
            {personalInfo.firstName && personalInfo.lastName && (
                <Typography className="headerName">
                {BaseLayout.safeRender(personalInfo.firstName)} {BaseLayout.safeRender(personalInfo.lastName)}
                </Typography>
            )}
            {personalInfo.professionalTitle && (
                <Typography className="headerTitle">
                {BaseLayout.safeRender(personalInfo.professionalTitle)}
                </Typography>
            )}
        </Box>
    );

    // Define contactBlock with conditional sx styles
    const contactBlock = (
        <Box 
          className={classes.contactSection} // Base class with flex, wrap, gap, margin
          // Conditional styling based on alignment
          sx={ 
            headerAlignment === 'center' 
            ? { justifyContent: 'center' } // Center items horizontally for centered row (keep flexbox)
            : { // Grid layout for Left/Right layouts
                display: 'grid', // Override flex from class
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', // 2 equal columns
                gap: '5px 10px', // Row gap 5px, column gap 10px
                alignItems: 'start',
                justifyItems: 'start', // Always left-align text within grid cells for readability
                marginTop: '5px', // Adjust margin for grid
                width: 'fit-content', // Only take up necessary space
                marginLeft: headerAlignment === 'left' ? '0' : 'auto', // Position grid appropriately
                marginRight: headerAlignment === 'left' ? 'auto' : '0'
              } 
          }
        >
            {/* Contact items with conditional styling for grid span when alone */}
            {personalInfo.phone && (
                <Box 
                  className="contactItem"
                  // If this is the only contact item, make it span both columns
                  sx={
                    headerAlignment !== 'center' && 
                    !personalInfo.email && 
                    !personalInfo.linkedin && 
                    !personalInfo.github && 
                    !personalInfo.address
                      ? { gridColumn: 'span 2', justifySelf: 'center' }
                      : {}
                  }
                >
                {activeStyle?.settings?.useContactIcons && <FontAwesomeIcon icon={faPhone} className={classes.contactIcon} />}
                {BaseLayout.safeRender(personalInfo.phone)}
                </Box>
            )}
            {personalInfo.email && (
                <Box 
                  className="contactItem"
                  // If this is the only contact item, make it span both columns
                  sx={
                    headerAlignment !== 'center' && 
                    !personalInfo.phone && 
                    !personalInfo.linkedin && 
                    !personalInfo.github && 
                    !personalInfo.address
                      ? { gridColumn: 'span 2', justifySelf: 'center' }
                      : {}
                  }
                >
                {activeStyle?.settings?.useContactIcons && <FontAwesomeIcon icon={faAt} className={classes.contactIcon} />}
                {BaseLayout.safeRender(personalInfo.email)}
                </Box>
            )}
            {personalInfo.linkedin && (
                <Box 
                  className="contactItem"
                  // If this is the only contact item, make it span both columns
                  sx={
                    headerAlignment !== 'center' && 
                    !personalInfo.phone && 
                    !personalInfo.email && 
                    !personalInfo.github && 
                    !personalInfo.address
                      ? { gridColumn: 'span 2', justifySelf: 'center' }
                      : {}
                  }
                >
                {activeStyle?.settings?.useContactIcons && <FontAwesomeIcon icon={faLinkedin} className={classes.contactIcon} />}
                {BaseLayout.safeRender(personalInfo.linkedin)}
                </Box>
            )}
            {personalInfo.github && (
                <Box 
                  className="contactItem"
                  // If this is the only contact item, make it span both columns
                  sx={
                    headerAlignment !== 'center' && 
                    !personalInfo.phone && 
                    !personalInfo.email && 
                    !personalInfo.linkedin && 
                    !personalInfo.address
                      ? { gridColumn: 'span 2', justifySelf: 'center' }
                      : {}
                  }
                >
                {activeStyle?.settings?.useContactIcons && <FontAwesomeIcon icon={faGithub} className={classes.contactIcon} />}
                {BaseLayout.safeRender(personalInfo.github)}
                </Box>
            )}
            {personalInfo.address && (
                <Box 
                  className="contactItem"
                  // If this is the only contact item, make it span both columns
                  sx={
                    headerAlignment !== 'center' && 
                    !personalInfo.phone && 
                    !personalInfo.email && 
                    !personalInfo.linkedin && 
                    !personalInfo.github
                      ? { gridColumn: 'span 2', justifySelf: 'center' }
                      : {}
                  }
                >
                {activeStyle?.settings?.useContactIcons && <FontAwesomeIcon icon={faMapMarkerAlt} className={classes.contactIcon} />}
                {BaseLayout.safeRender(personalInfo.address)}
                </Box>
            )}
        </Box>
    );

     // Photo Block - Fixed size via sx, use class for border and other styles
     const photoBlock = (() => {
       // Don't show photo space if enablePictureSpace is false
       if (personalInfo.enablePictureSpace === false) {
         return null;
       }
       
       // Show actual photo if it exists
       if (personalInfo.photo) {
         return (
           <Avatar
             src={personalInfo.photo}
             alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
             className={classes.profilePhoto} // Use class for border and base styles
             // Specific overrides for side layouts
             sx={{
                 width: '100px', // Explicitly set size to ensure consistency
                 height: '100px',
                 margin: 0, // Override auto margin from class
                 flexShrink: 0, // Prevent shrinking
             }}
           />
         );
       }
       
       // Show invisible placeholder when enablePictureSpace is true but no photo
       return (
         <Box
           sx={{
             width: '100px',
             height: '100px',
             margin: 0,
             flexShrink: 0,
             visibility: 'hidden', // Invisible but takes up space
           }}
         />
       );
     })();


    // Structure for Centered (Render Avatar with class and explicit size)
    if (headerAlignment === 'center') {
        return (
            <>
                {(() => {
                  // Don't show photo space if enablePictureSpace is false
                  if (personalInfo.enablePictureSpace === false) {
                    return null;
                  }
                  
                  // Show actual photo if it exists
                  if (personalInfo.photo) {
                    return (
                      <Avatar
                        src={personalInfo.photo}
                        alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
                        className={classes.profilePhoto} // Use class for border and base styles
                        // Explicit size for consistency
                        sx={{ 
                          width: '100px',
                          height: '100px',
                          // Keep margin from class
                        }}
                      />
                    );
                  }
                  
                  // Show invisible placeholder when enablePictureSpace is true but no photo
                  return (
                    <Box
                      sx={{
                        width: '100px',
                        height: '100px',
                        margin: '0 auto 15px', // Match class margin
                        visibility: 'hidden', // Invisible but takes up space
                      }}
                    />
                  );
                })()}
                {/* Render nameBlock and centered contactBlock */} 
                {nameBlock}
                {contactBlock} 
            </>
        );
    }

    // --- Structure for Left/Right Aligned --- 
    // Combine Name and Contact into a details block
    const contentDetailsBlock = (
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            // Controls internal text alignment (left or right)
            alignItems: headerAlignment === 'left' ? 'flex-start' : 'flex-end',
            flex: '1 1 auto', // Allows growth
          }}
        >
            {nameBlock}
            {contactBlock}
        </Box>
    );

    // --- Explicitly control render order for Left/Right --- 
    if (headerAlignment === 'left') {
        // Left Layout: Content Block | Photo Block
        return (
          <>
            {contentDetailsBlock}
            {(() => {
              // Don't show photo space if enablePictureSpace is false
              if (personalInfo.enablePictureSpace === false) {
                return null;
              }
              
              // Show actual photo if it exists
              if (personalInfo.photo) {
                return (
                  <Avatar
                    src={personalInfo.photo}
                    alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
                    sx={{
                      width: '100px', 
                      height: '100px',
                      // Use sidebarPrimary for photo border in HeaderContent, else main primary
                      border: `3px solid ${design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000'}`,
                      margin: '0',
                    }} 
                  />
                );
              }
              
              // Show invisible placeholder when enablePictureSpace is true but no photo
              return (
                <Box
                  sx={{
                    width: '100px',
                    height: '100px',
                    margin: '0',
                    flexShrink: 0,
                    visibility: 'hidden', // Invisible but takes up space
                  }}
                />
              );
            })()}
          </>
        );
    } else { // Right alignment
        // Right Layout: Photo Block | Content Block
        return (
          <>
            {(() => {
              // Don't show photo space if enablePictureSpace is false
              if (personalInfo.enablePictureSpace === false) {
                return null;
              }
              
              // Show actual photo if it exists
              if (personalInfo.photo) {
                return (
                  <Avatar
                    src={personalInfo.photo}
                    alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
                    sx={{
                      width: '100px', 
                      height: '100px',
                      // Use sidebarPrimary for photo border in HeaderContent, else main primary
                      border: `3px solid ${design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000'}`,
                      margin: '0',
                    }} 
                  />
                );
              }
              
              // Show invisible placeholder when enablePictureSpace is true but no photo
              return (
                <Box
                  sx={{
                    width: '100px',
                    height: '100px',
                    margin: '0',
                    flexShrink: 0,
                    visibility: 'hidden', // Invisible but takes up space
                  }}
                />
              );
            })()}
            {contentDetailsBlock}
          </>
        );
    }
    // --- END Structure for Left/Right --- 

  };

  // --- Background is now handled centrally by LayoutResolver --- 
  // All layouts should use transparent background to let centrally-rendered backgrounds show through
  const layoutBackgroundColor = selectedBackground === 'blank' 
                                ? (design?.colors?.background || '#ffffff') 
                                : 'transparent';

  console.log('HeaderLayoutBase - selectedBackground:', selectedBackground, 'layoutBackgroundColor:', layoutBackgroundColor);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '210mm',
        minHeight: '297mm',
        padding: '15mm',
        boxSizing: 'border-box',
        // --- Use the conditionally determined background color --- 
        backgroundColor: layoutBackgroundColor,
        color: design?.colors?.text || '#000',
        ...typographyStyles,
        ...(measureMode ? { position: 'absolute', visibility: 'hidden' } : {}),
        ...(isPdfExport ? { position: 'relative', visibility: 'visible' } : {}),
        "& *": {
           maxWidth: "180mm",
           wordWrap: "break-word",
           overflowWrap: "break-word",
         }
      }}
    >
      {currentPage === 0 && (
        <Box sx={getHeaderContainerStyles()} data-section="header">
          {renderHeaderContent()}
        </Box>
      )}

      {/* --- NEW: Explicit Section Rendering using Switch --- */}
      {selectedSections.map((section) => {
        // Use BaseLayout renderers - these should apply appropriate classes
        switch(section.name) {
          case 'Professional Summary':
            return BaseLayout.renderProfessionalSummary(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'Work Experience':
            return BaseLayout.renderWorkExperiences(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'Education':
            return BaseLayout.renderEducations(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'Skills': // Now correctly rendered
            return BaseLayout.renderSkills(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, activeBarStyle, t);
          case 'Projects': // Now correctly rendered
            return BaseLayout.renderProjects(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'Certifications': // Now correctly rendered
            return BaseLayout.renderCertifications(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'Awards and Honors': // MODIFIED HERE - Now correctly rendered
            return BaseLayout.renderAwardsAndHonors(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'Languages': // Now correctly rendered
            return BaseLayout.renderLanguages(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, activeBarStyle, t);
          case 'Volunteer Experience':
            return BaseLayout.renderVolunteerExperiences(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'Publications': // Now correctly rendered
            return BaseLayout.renderPublications(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          case 'References':
            return BaseLayout.renderReferences(formData, classes, design?.colors, activeStyle, 'classic', selectedBackground, selectedSpecialEffectId, t);
          default:
             console.warn(`Render function not explicitly mapped for section: ${section.name}`);
            return null;
        }
      })}
      {/* --- END: Explicit Section Rendering --- */}

      {totalPages > 1 && !measureMode && (
        <Typography
          className="smallText"
          sx={{
            position: 'absolute',
            bottom: '10mm',
            right: '10mm',
            color: design?.colors?.textSecondary || '#666',
          }}
        >
          Page {currentPage + 1} of {totalPages}
        </Typography>
      )}
    </Box>
  );
};

// --- Extracted reusable header content component (Memoized) ---
const HeaderContentComponent = ({
  formData,
  design,
  activeStyle,
  headerAlignment = 'center',
}) => {
  console.log('HeaderContent alignment:', headerAlignment); // DEBUG LOG
  const { personalInfo = {} } = formData;

  const headerNameColor = design?.colors?.sidebarText || design?.colors?.primary || '#000';
  const headerTitleColor = design?.colors?.sidebarText || design?.colors?.secondary || '#333';
  const contactItemTextColor = design?.colors?.sidebarText || design?.colors?.text || '#000';
  const contactIconColor = design?.colors?.sidebarPrimary || design?.colors?.primary || '#000';

  const nameBlock = (
    <Box>
      {personalInfo.firstName && personalInfo.lastName && (
        <Typography className="headerName" sx={{ color: headerNameColor }}>
          {BaseLayout.safeRender(personalInfo.firstName)} {BaseLayout.safeRender(personalInfo.lastName)}
        </Typography>
      )}
      {personalInfo.professionalTitle && (
        <Typography className="headerTitle" sx={{ color: headerTitleColor }}>
          {BaseLayout.safeRender(personalInfo.professionalTitle)}
        </Typography>
      )}
    </Box>
  );

  const contactBlock = (
    <Box
      sx={ // Outer Box styling for alignment and grid structure
        headerAlignment === 'center'
          ? { justifyContent: 'center', display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '10px' }
          : {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '5px 10px',
              alignItems: 'start',
              justifyItems: 'start',
              marginTop: '5px',
              width: 'fit-content',
              marginLeft: headerAlignment === 'left' ? '0' : 'auto',
              marginRight: headerAlignment === 'left' ? 'auto' : '0',
            }
      }
    >
      {personalInfo.phone && (
        <Box
          className="contactItem"
          sx={headerAlignment !== 'center' && !personalInfo.email && !personalInfo.linkedin && !personalInfo.github && !personalInfo.address ? { gridColumn: 'span 2', justifySelf: 'center' } : {}}
        >
          {activeStyle?.settings?.useContactIcons && (
            <FontAwesomeIcon icon={faPhone} style={{ color: contactIconColor, fontSize: '16px', marginRight: '5px' }} />
          )}
          <Typography component="span" className="contactItem" sx={{ color: contactItemTextColor }}>{BaseLayout.safeRender(personalInfo.phone)}</Typography>
        </Box>
      )}
      {personalInfo.email && (
        <Box
          className="contactItem"
          sx={headerAlignment !== 'center' && !personalInfo.phone && !personalInfo.linkedin && !personalInfo.github && !personalInfo.address ? { gridColumn: 'span 2', justifySelf: 'center' } : {}}
        >
          {activeStyle?.settings?.useContactIcons && (
            <FontAwesomeIcon icon={faAt} style={{ color: contactIconColor, fontSize: '16px', marginRight: '5px' }} />
          )}
          <Typography component="span" className="contactItem" sx={{ color: contactItemTextColor }}>{BaseLayout.safeRender(personalInfo.email)}</Typography>
        </Box>
      )}
      {personalInfo.linkedin && (
        <Box
          className="contactItem"
          sx={headerAlignment !== 'center' && !personalInfo.phone && !personalInfo.email && !personalInfo.github && !personalInfo.address ? { gridColumn: 'span 2', justifySelf: 'center' } : {}}
        >
          {activeStyle?.settings?.useContactIcons && (
            <FontAwesomeIcon icon={faLinkedin} style={{ color: contactIconColor, fontSize: '16px', marginRight: '5px' }} />
          )}
          <Typography component="span" className="contactItem" sx={{ color: contactItemTextColor }}>{BaseLayout.safeRender(personalInfo.linkedin)}</Typography>
        </Box>
      )}
      {personalInfo.github && (
        <Box
          className="contactItem"
          sx={headerAlignment !== 'center' && !personalInfo.phone && !personalInfo.email && !personalInfo.linkedin && !personalInfo.address ? { gridColumn: 'span 2', justifySelf: 'center' } : {}}
        >
          {activeStyle?.settings?.useContactIcons && (
            <FontAwesomeIcon icon={faGithub} style={{ color: contactIconColor, fontSize: '16px', marginRight: '5px' }} />
          )}
          <Typography component="span" className="contactItem" sx={{ color: contactItemTextColor }}>{BaseLayout.safeRender(personalInfo.github)}</Typography>
        </Box>
      )}
      {personalInfo.address && (
        <Box
          className="contactItem"
          sx={headerAlignment !== 'center' && !personalInfo.phone && !personalInfo.email && !personalInfo.linkedin && !personalInfo.github ? { gridColumn: 'span 2', justifySelf: 'center' } : {}}
        >
          {activeStyle?.settings?.useContactIcons && (
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: contactIconColor, fontSize: '16px', marginRight: '5px' }} />
          )}
          <Typography component="span" className="contactItem" sx={{ color: contactItemTextColor }}>{BaseLayout.safeRender(personalInfo.address)}</Typography>
        </Box>
      )}
    </Box>
  );

  // Photo block
  const photoBlock = (() => {
    // Don't show photo space if enablePictureSpace is false
    if (personalInfo.enablePictureSpace === false) {
      return null;
    }
    
    // Show actual photo if it exists
    if (personalInfo.photo) {
      return (
        <Avatar
          src={personalInfo.photo}
          alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
          sx={{
            width: '100px', 
            height: '100px',
            // Use sidebarPrimary for photo border in HeaderContent, else main primary
            border: `3px solid ${design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000'}`,
            margin: '0 auto 15px',
          }} 
        />
      );
    }
    
    // Show invisible placeholder when enablePictureSpace is true but no photo
    return (
      <Box
        sx={{
          width: '100px',
          height: '100px',
          margin: '0 auto 15px',
          visibility: 'hidden', // Invisible but takes up space
        }}
      />
    );
  })();

  // Structure for Centered
  if (headerAlignment === 'center') {
    return (
      <>
        {(() => {
          // Don't show photo space if enablePictureSpace is false
          if (personalInfo.enablePictureSpace === false) {
            return null;
          }
          
          // Show actual photo if it exists
          if (personalInfo.photo) {
            return (
              <Avatar
                src={personalInfo.photo}
                alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
                sx={{
                  width: '100px', 
                  height: '100px',
                  // Use sidebarPrimary for photo border in HeaderContent, else main primary
                  border: `3px solid ${design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000'}`,
                  margin: '0 auto 15px',
                }} 
              />
            );
          }
          
          // Show invisible placeholder when enablePictureSpace is true but no photo
          return (
            <Box
              sx={{
                width: '100px',
                height: '100px',
                margin: '0 auto 15px',
                visibility: 'hidden', // Invisible but takes up space
              }}
            />
          );
        })()}
        {nameBlock}
        {contactBlock}
      </>
    );
  }

  // Structure for Left/Right
  const contentDetailsBlock = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: headerAlignment === 'left' ? 'flex-start' : 'flex-end',
        flex: '1 1 auto',
      }}
    >
      {nameBlock}
      {contactBlock}
    </Box>
  );

  if (headerAlignment === 'left') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        {contentDetailsBlock}
        {(() => {
          // Don't show photo space if enablePictureSpace is false
          if (personalInfo.enablePictureSpace === false) {
            return null;
          }
          
          // Show actual photo if it exists
          if (personalInfo.photo) {
            return (
              <Avatar
                src={personalInfo.photo}
                alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
                sx={{
                  width: '100px', 
                  height: '100px',
                  // Use sidebarPrimary for photo border in HeaderContent, else main primary
                  border: `3px solid ${design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000'}`,
                  margin: '0',
                }} 
              />
            );
          }
          
          // Show invisible placeholder when enablePictureSpace is true but no photo
          return (
            <Box
              sx={{
                width: '100px',
                height: '100px',
                margin: '0',
                flexShrink: 0,
                visibility: 'hidden', // Invisible but takes up space
              }}
            />
          );
        })()}
      </Box>
    );
  } else {
    // Right alignment
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        {(() => {
          // Don't show photo space if enablePictureSpace is false
          if (personalInfo.enablePictureSpace === false) {
            return null;
          }
          
          // Show actual photo if it exists
          if (personalInfo.photo) {
            return (
              <Avatar
                src={personalInfo.photo}
                alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
                sx={{
                  width: '100px', 
                  height: '100px',
                  // Use sidebarPrimary for photo border in HeaderContent, else main primary
                  border: `3px solid ${design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000'}`,
                  margin: '0',
                }} 
              />
            );
          }
          
          // Show invisible placeholder when enablePictureSpace is true but no photo
          return (
            <Box
              sx={{
                width: '100px',
                height: '100px',
                margin: '0',
                flexShrink: 0,
                visibility: 'hidden', // Invisible but takes up space
              }}
            />
          );
        })()}
        {contentDetailsBlock}
      </Box>
    );
  }
};

export const HeaderContent = React.memo(HeaderContentComponent);

export default HeaderLayoutBase; 